// The canonical Connect web host. Mirrors the server's PROD_ROOT_DOMAIN
// (apps/server/src/config.ts): connect.leanin.org and its subdomains
// (e.g. dev.connect.leanin.org) are the Connect app itself. leanin.org — the
// marketing site — is deliberately NOT included: it is a separate site, so its
// links stay external and keep the new-tab + interstitial treatment.
const CONNECT_ROOT_DOMAIN = 'connect.leanin.org';

// Dummy base used only to parse a root-relative href into its pathname/search/
// hash. Never contributes to the returned value, which is always relative.
const RELATIVE_PARSE_BASE = 'http://internal.invalid';

function isConnectHost(hostname: string): boolean {
  return hostname === CONNECT_ROOT_DOMAIN || hostname.endsWith(`.${CONNECT_ROOT_DOMAIN}`);
}

function parse(href: string, base: string | undefined): URL | null {
  try {
    return base === undefined ? new URL(href) : new URL(href, base);
  } catch {
    return null;
  }
}

// Decides whether a link inside user/member-authored content navigates within
// the Connect SPA, and if so, to which in-app path. "Internal" means: a
// root-relative path, a same-origin absolute URL, or an absolute URL to a
// Connect host. Returns the in-app destination (pathname + search + hash) for
// internal links, or null for anything external (other http(s) hosts,
// protocol-relative //host, mailto:/tel:, unparseable input).
//
// `currentOrigin` is `window.location.origin` in the browser, or null when no
// window exists (render-to-string). Pure and string-only — the same-origin
// check is the only origin-dependent branch, so relative and Connect-host
// links resolve identically with or without a window.
export function internalPath(href: string, currentOrigin: string | null): string | null {
  // Root-relative path (but not protocol-relative //host) — unambiguously
  // internal without needing an origin.
  if (href.startsWith('/') && !href.startsWith('//')) {
    const relative = parse(href, RELATIVE_PARSE_BASE);
    return relative === null ? null : `${relative.pathname}${relative.search}${relative.hash}`;
  }

  const url = parse(href, undefined);
  if (url === null) return null;
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

  const sameOrigin = currentOrigin !== null && url.origin === currentOrigin;
  if (sameOrigin || isConnectHost(url.hostname)) {
    return `${url.pathname}${url.search}${url.hash}`;
  }
  return null;
}
